import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, IconButton, Drawer, List, ListItemButton, ListItemText, LinearProgress, Box, ListItemIcon, Collapse, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EventIcon from '@mui/icons-material/Event';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { logout } from '../utils/auth';
import { fetchCurrentUser } from '../services/auth';
import { useBranding } from '../context/BrandingContext';

const MainLayout = () => {
  const navigate = useNavigate();
  const BRAND = useBranding();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await fetchCurrentUser(true); // Force refresh pour éviter le cache
      if (currentUser) {
        const roleId = Number(currentUser?.role_id ?? currentUser?.role);
        setUserRole(roleId);
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: 1300 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', height: '80px', minHeight: '80px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {BRAND.clientLogo && (
              <img
                src={BRAND.clientLogo}
                alt="Logo"
                style={{ height: '60px', marginRight: '20px' }}
              />
            )}
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <MenuIcon />
            </IconButton>
            <Button color="inherit" component={Link} to="/dashboard">
              Tableau de bord
            </Button>
          </div>

          <div>
            <Button color="inherit" component={Link} to="/change-password">
              Changer MDP
            </Button>
            <Button color="inherit" onClick={handleLogout}>
              Déconnexion
            </Button>
          </div>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: 300,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 300,
            boxSizing: 'border-box',
            backgroundColor: 'primary.main',
            marginTop: '80px',
            height: 'calc(100vh - 80px)',
            position: 'fixed',
            zIndex: 1200
          },
        }}
        variant="temporary"
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <List sx={{ padding: '20px 0' }}>
          {/* User Links */}
          <ListItemButton component={Link} to="/exit-authorizations" onClick={() => setSidebarOpen(false)} sx={{ 
            color: 'white', 
            '&:hover': { backgroundColor: 'primary.dark' },
            mx: 2,
            my: 0.5,
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out'
          }}>
            <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Autorisations de Sortie" 
              sx={{ 
                color: 'white',
                '& .MuiListItemText-primary': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left'
                }
              }} 
            />
          </ListItemButton>
          <ListItemButton component={Link} to="/entry-authorizations" onClick={() => setSidebarOpen(false)} sx={{ 
            color: 'white', 
            '&:hover': { backgroundColor: 'primary.dark' },
            mx: 2,
            my: 0.5,
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out'
          }}>
            <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Autorisations d'Entrée" 
              sx={{ 
                color: 'white',
                '& .MuiListItemText-primary': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left'
                }
              }} 
            />
          </ListItemButton>
          <ListItemButton component={Link} to="/mission-orders" onClick={() => setSidebarOpen(false)} sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
                mx: 2,
                my: 0.5,
                borderRadius: 1,
                transition: 'all 0.2s ease-in-out'
              }}>
                <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                  <FlightTakeoffIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Missions"
                  sx={{
                    color: 'white',
                    '& .MuiListItemText-primary': {
                      fontSize: '1rem',
                      fontWeight: 500,
                      textAlign: 'left'
                    }
                  }}
                />
              </ListItemButton>
          <ListItemButton component={Link} to="/decharge-argents" onClick={() => setSidebarOpen(false)} sx={{ 
            color: 'white', 
            '&:hover': { backgroundColor: 'primary.dark' },
            mx: 2,
            my: 0.5,
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out'
          }}>
            <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
              <MoneyOffIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Décharges" 
              sx={{ 
                color: 'white',
                '& .MuiListItemText-primary': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left'
                }
              }} 
            />
          </ListItemButton>
          {userRole === 1 && (
            <ListItemButton component={Link} to="/contracts" onClick={() => setSidebarOpen(false)} sx={{ 
              color: 'white', 
              '&:hover': { backgroundColor: 'primary.dark' },
              mx: 2,
              my: 0.5,
              borderRadius: 1,
              transition: 'all 0.2s ease-in-out'
            }}>
              <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                <DescriptionIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Contrats" 
                sx={{ 
                  color: 'white',
                  '& .MuiListItemText-primary': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    textAlign: 'left'
                  }
                }} 
              />
            </ListItemButton>
          )}
          <ListItemButton component={Link} to="/leaves" onClick={() => setSidebarOpen(false)} sx={{ 
            color: 'white', 
            '&:hover': { backgroundColor: 'primary.dark' },
            mx: 2,
            my: 0.5,
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out'
          }}>
            <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
              <BeachAccessIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Absences/Congés" 
              sx={{ 
                color: 'white',
                '& .MuiListItemText-primary': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left'
                }
              }} 
            />
          </ListItemButton>
          <ListItemButton component={Link} to="/reports" onClick={() => setSidebarOpen(false)} sx={{ 
            color: 'white', 
            '&:hover': { backgroundColor: 'primary.dark' },
            mx: 2,
            my: 0.5,
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out'
          }}>
            <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Documents Administratifs" 
              sx={{ 
                color: 'white',
                '& .MuiListItemText-primary': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left'
                }
              }} 
            />
          </ListItemButton>

          {/* Admin Links */}
          {(userRole === 1 || userRole === 3) && (
            <React.Fragment>
              <ListItemButton component={Link} to="/employees" onClick={() => setSidebarOpen(false)} sx={{ 
                color: 'white', 
                '&:hover': { backgroundColor: 'primary.dark' },
                mx: 2,
                my: 0.5,
                borderRadius: 1,
                transition: 'all 0.2s ease-in-out'
              }}>
                <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Gestion des Employés" 
                  sx={{ 
                    color: 'white',
                    '& .MuiListItemText-primary': {
                      fontSize: '1rem',
                      fontWeight: 500,
                      textAlign: 'left'
                    }
                  }} 
                />
              </ListItemButton>

              {/* Données de base Section */}
              <Accordion sx={{ 
                mx: 2,
                my: 0.5,
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:before': { display: 'none' }
              }}>
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  sx={{ 
                    color: 'white',
                    '& .MuiAccordionSummary-content': {
                      margin: '8px 0'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                    <DescriptionIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Données de base" 
                    sx={{ 
                      color: 'white',
                      '& .MuiListItemText-primary': {
                        fontSize: '1rem',
                        fontWeight: 600,
                        textAlign: 'left'
                      }
                    }} 
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0, pl: 2 }}>
                  <List sx={{ padding: 0 }}>
                    <ListItemButton component={Link} to="/payslips" onClick={() => setSidebarOpen(false)} sx={{ 
                      color: 'white', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: 1,
                      transition: 'all 0.2s ease-in-out',
                      mb: 0.5
                    }}>
                      <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                        <DescriptionIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Fiches de Paie" 
                        sx={{ 
                          color: 'white',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                            fontWeight: 400,
                            textAlign: 'left'
                          }
                        }} 
                      />
                    </ListItemButton>
                    
                    {userRole === 1 && (
                      <ListItemButton component={Link} to="/documents-legaux" onClick={() => setSidebarOpen(false)} sx={{ 
                        color: 'white', 
                        '&:hover': { backgroundColor: 'primary.dark' },
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        mb: 0.5
                      }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                          <DescriptionIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Documents Légaux" 
                          sx={{ 
                            color: 'white',
                            '& .MuiListItemText-primary': {
                              fontSize: '0.9rem',
                              fontWeight: 400,
                              textAlign: 'left'
                            }
                          }} 
                        />
                      </ListItemButton>
                    )}
                    
                    {userRole === 1 && (
                      <ListItemButton component={Link} to="/sites" onClick={() => setSidebarOpen(false)} sx={{ 
                        color: 'white', 
                        '&:hover': { backgroundColor: 'primary.dark' },
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        mb: 0.5
                      }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                          <BusinessIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Sites" 
                          sx={{ 
                            color: 'white',
                            '& .MuiListItemText-primary': {
                              fontSize: '0.9rem',
                              fontWeight: 400,
                              textAlign: 'left'
                            }
                          }} 
                        />
                      </ListItemButton>
                    )}
                    
                    {userRole === 1 && (
                      <ListItemButton component={Link} to="/professions" onClick={() => setSidebarOpen(false)} sx={{ 
                        color: 'white', 
                        '&:hover': { backgroundColor: 'primary.dark' },
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        mb: 0.5
                      }}>
                        <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                          <WorkIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Services" 
                          sx={{ 
                            color: 'white',
                            '& .MuiListItemText-primary': {
                              fontSize: '0.9rem',
                              fontWeight: 400,
                              textAlign: 'left'
                            }
                          }} 
                        />
                      </ListItemButton>
                    )}
                    
                    <ListItemButton component={Link} to="/transports" onClick={() => setSidebarOpen(false)} sx={{ 
                      color: 'white', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: 1,
                      transition: 'all 0.2s ease-in-out',
                      mb: 0.5
                    }}>
                      <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                        <DirectionsCarIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Transports" 
                        sx={{ 
                          color: 'white',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                            fontWeight: 400,
                            textAlign: 'left'
                          }
                        }} 
                      />
                    </ListItemButton>
                    
                    <ListItemButton component={Link} to="/holidays" onClick={() => setSidebarOpen(false)} sx={{ 
                      color: 'white', 
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: 1,
                      transition: 'all 0.2s ease-in-out',
                      mb: 0.5
                    }}>
                      <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                        <EventIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Jours Fériés" 
                        sx={{ 
                          color: 'white',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                            fontWeight: 400,
                            textAlign: 'left'
                          }
                        }} 
                      />
                    </ListItemButton>
                  </List>
                </AccordionDetails>
              </Accordion>

              {userRole === 1 && (
                <ListItemButton component={Link} to="/settings" onClick={() => setSidebarOpen(false)} sx={{ 
                  color: 'white', 
                  '&:hover': { backgroundColor: 'primary.dark' },
                  mx: 2,
                  my: 0.5,
                  borderRadius: 1,
                  transition: 'all 0.2s ease-in-out'
                }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Paramètres" 
                    sx={{ 
                      color: 'white',
                      '& .MuiListItemText-primary': {
                        fontSize: '1rem',
                        fontWeight: 500,
                        textAlign: 'left'
                      }
                    }} 
                  />
                </ListItemButton>
              )}
              <ListItemButton component={Link} to="/inactifs" onClick={() => setSidebarOpen(false)} sx={{ 
                color: 'white', 
                '&:hover': { backgroundColor: 'primary.dark' },
                mx: 2,
                my: 0.5,
                borderRadius: 1,
                transition: 'all 0.2s ease-in-out'
              }}>
                <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                  <PersonOffIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Employés Inactifs" 
                  sx={{ 
                    color: 'white',
                    '& .MuiListItemText-primary': {
                      fontSize: '1rem',
                      fontWeight: 500,
                      textAlign: 'left'
                    }
                  }} 
                />
              </ListItemButton>
            </React.Fragment>
          )}
        </List>
      </Drawer>

      <Box component="main" sx={{ 
        padding: '20px', 
        marginTop: '80px',
        minHeight: 'calc(100vh - 80px)'
      }}>
        <Outlet />
      </Box>
    </>
  );
};

export default MainLayout;